import { Component, input, signal, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { ApiService } from '../../../Utils/_services/api/api.service';
import { environment } from '../../../environments/environment';

interface BubbleImage {
  url: string;
  bubbleIndex: number;
  visible: boolean;
  fading: boolean;
}

@Component({
  selector: 'app-left-ui',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './left-ui.component.html',
  styleUrl: './left-ui.component.css'
})
export class LeftUiComponent implements OnInit, OnDestroy {
  welcomeText = input<string>("Join Our Platform");
  welcomeSubtitle = input<string>(
    "Start your journey with us today and unlock amazing features"
  );
  enableBubbleEffect = input<boolean>(true);
  
  totalBubbles = 5;
  bubbleImages = signal<BubbleImage[]>(
    Array.from({ length: 5 }, (_, i) => ({
      url: "",
      bubbleIndex: i,
      visible: false,
      fading: false,
    }))
  );

  // Will be fetched from API
  private fetchedImages: string[] = [];

  private imageQueue: string[] = [];
  private intervalRef: any;
  private fadeOutTimeout: any;
  readonly DISPLAY_DURATION = 8000;
  readonly FADE_DURATION = 2000;
  private lastBubbleIndex: number = -1;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    if (this.enableBubbleEffect()) {
      this.fetchImagesAndStart();
    }
  }

  fetchImagesAndStart(): void {
    const params = new HttpParams({ fromObject: { category: 'auth' } });
    this.apiService.get<any>({
      BaseUrl: environment.adoreBaseUrl,
      Url: "dating/brand-images",
      Params: params
    }).subscribe({
      next: (res: any) => {
        const dataArray = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        
        if (dataArray.length) {
          // Only get 'Circle' or 'circle' type images
          const validImages = dataArray.filter((img: any) => 
            img.imageType === 'circle' || img.imageType === 'Circle'
          );
          
          if (validImages.length > 0) {
            // Map to URLs
            this.fetchedImages = validImages.map((img: any) => img.imageUrl || img.image?.signedUrl || '');
            this.fetchedImages = this.fetchedImages.filter((url: string) => url !== '');
            
            if (this.fetchedImages.length > 0) {
              this.imageQueue = this.shuffleArray([...this.fetchedImages]);
              this.startBubbleCycle();
            }
          }
        }
      },
      error: (err) => {
        console.error('Error fetching brand images for left ui:', err);
      }
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalRef);
    clearTimeout(this.fadeOutTimeout);
  }

  startBubbleCycle(): void {
    if (!this.imageQueue.length) return;
    this.showNextImage();
    this.intervalRef = setInterval(() => {
      this.showNextImage();
    }, this.DISPLAY_DURATION + this.FADE_DURATION);
  }

  pickRandomBubbleIndex(): number {
    let next: number;
    do {
      next = Math.floor(Math.random() * this.totalBubbles);
    } while (next === this.lastBubbleIndex);
    this.lastBubbleIndex = next;
    return next;
  }

  showNextImage(): void {
    if (!this.imageQueue.length) {
      this.imageQueue = this.shuffleArray([...this.fetchedImages]);
    }

    const nextUrl = this.imageQueue.shift()!;
    const nextBubble = this.pickRandomBubbleIndex();

    this.bubbleImages.set(
      this.bubbleImages().map((b) =>
        b.visible ? { ...b, fading: true } : b
      )
    );

    this.fadeOutTimeout = setTimeout(() => {
      this.bubbleImages.set(
        this.bubbleImages().map((b, i) => {
          if (i === nextBubble) {
            return { ...b, url: nextUrl, visible: true, fading: false };
          }
          return { ...b, url: '', visible: false, fading: false };
        })
      );
    }, this.FADE_DURATION);
  }

  shuffleArray<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }
}
